// Navegación del shell «Orion». Cada entrada apunta a módulos/pestañas que YA
// existen en el registry; se muestra solo si la org/usuario tiene alguno de sus
// destinos disponible (mismo gating de siempre: ModulesContext + requireAdmin).
// Así el shell nunca ofrece algo que el backend negaría.

import type { ReactNode } from 'react';
import {
  BadgeDollarSign,
  BarChart3,
  Bot,
  CalendarDays,
  Cctv,
  ClipboardCheck,
  FileText,
  GitPullRequestArrow,
  Globe,
  HeartPulse,
  Landmark,
  Home,
  LifeBuoy,
  PawPrint,
  Settings,
  Users,
} from 'lucide-react';

import type { ModuleDefinition } from '@/modules';
import { WhatsAppGlyph } from '@/modules/whatsapp-app/components/WhatsAppGlyph';

export interface ShellNavItem {
  key: string;
  label: string;
  icon: ReactNode;
  /** destinos 'moduleKey/tab' en orden de preferencia */
  targets: string[];
  /** módulos cuyo contenido «pertenece» a esta entrada (para marcarla activa) */
  owns: string[];
  /** sub-entrada (se pinta indentada bajo la anterior) */
  child?: boolean;
  /** solo admin/dev (el backend igual lo exige) */
  adminOnly?: boolean;
  /** solo si la org tiene este módulo disponible */
  requiresModule?: string;
  /** oculto en orgs soberanas (servicio comercial extranjero, p. ej. Google) */
  hideForSovereign?: boolean;
}

const s = 18;

export const SHELL_NAV: ShellNavItem[] = [
  { key: 'inicio', label: 'Inicio', icon: <Home size={s} />, targets: ['orion/inicio'], owns: ['orion/inicio', 'orion/asistente'] },
  { key: 'agentes', label: 'Agentes', icon: <Bot size={s} />, targets: ['orion/agentes'], owns: ['orion/agentes', 'agentes'] },
  // El editor visual vive en el módulo orion (pantalla propia del shell), pero solo
  // para orgs con sitio: `requiresModule` exige el módulo `site` (site_config).
  { key: 'sitio', label: 'Editor del sitio', icon: <Globe size={s} />, targets: ['orion/editor-sitio', 'site/builder', 'site/theme'], owns: ['site', 'orion/editor-sitio'], requiresModule: 'site' },
  { key: 'cambios', label: 'Cambios del sitio', icon: <GitPullRequestArrow size={s} />, targets: ['orion/cambios-sitio'], owns: ['orion/cambios-sitio'], child: true, adminOnly: true },
  // Orion for Government: solo aparecen si la org tiene el módulo (gating de siempre).
  { key: 'presidente', label: 'Tablero del Presidente', icon: <Landmark size={s} />, targets: ['gov-executive/tablero'], owns: ['gov-executive'] },
  { key: 'salud', label: 'Salud · DIF', icon: <HeartPulse size={s} />, targets: ['gov-health/panorama'], owns: ['gov-health'] },
  { key: 'whatsapp', label: 'WhatsApp', icon: <WhatsAppGlyph size={s} title={null} />, targets: ['whatsapp-app/chats'], owns: ['whatsapp-app'] },
  { key: 'leads', label: 'Leads', icon: <Users size={s} />, targets: ['crm/leads', 'leads-import/crm'], owns: ['crm', 'leads-import'] },
  { key: 'calendario', label: 'Calendario', icon: <CalendarDays size={s} />, targets: ['orion/calendario'], owns: ['orion/calendario', 'bookings-1to1'], hideForSovereign: true },
  { key: 'contenido', label: 'Contenido', icon: <FileText size={s} />, targets: ['mkt-meta/social-publish', 'mkt-meta/approve'], owns: ['mkt-meta', 'mkt-ai', 'mkt-video'] },
  // Diagnósticos (ADR-007): solo si la org tiene el módulo y el usuario puede verlo
  // (admin o rol `consultor`; lo decide ModulesContext). Destino preferido: Proyectos.
  { key: 'diagnosticos', label: 'Diagnósticos', icon: <ClipboardCheck size={s} />, targets: ['diagnosticos/proyectos', 'diagnosticos/empresas', 'diagnosticos/programas'], owns: ['diagnosticos'], requiresModule: 'diagnosticos' },
  // Ventas cobradas con Stripe (TKT-00223): de administradores (el backend exige cobros:read).
  { key: 'ventas', label: 'Ventas', icon: <BadgeDollarSign size={s} />, targets: ['ventas/cobros'], owns: ['ventas'], adminOnly: true },
  // Orion for Government: aparecen solo si la org tiene el módulo y el usuario su rol (ModulesContext).
  { key: 'seguridad', label: 'Seguridad · C4', icon: <Cctv size={s} />, targets: ['gov-security/consola'], owns: ['gov-security'] },
  { key: 'bienestar', label: 'Bienestar Animal', icon: <PawPrint size={s} />, targets: ['gov-animal/ciba'], owns: ['gov-animal'] },
  { key: 'reportes', label: 'Reportes', icon: <BarChart3 size={s} />, targets: ['mkt-analytics/overview'], owns: ['mkt-analytics'] },
  { key: 'tickets', label: 'Tickets', icon: <LifeBuoy size={s} />, targets: ['orion/tickets'], owns: ['orion/tickets'], adminOnly: true },
  // Fija al fondo de la barra (OrionShell la saca de la lista). Para TODO usuario:
  // Mi perfil para cualquiera; prompt de la org, agente y módulos para admins
  // (dentro de la app). Empresa/Config siguen accesibles desde ahí.
  { key: 'config', label: 'Configuración', icon: <Settings size={s} />, targets: ['ajustes/inicio', 'empresa/company', 'config/guide'], owns: ['ajustes', 'empresa', 'config'] },
];

export interface Access {
  availableModules: ModuleDefinition[];
  isAdmin: boolean;
  isDev: boolean;
  /** org soberana (ModulesContext.sovereign) */
  sovereign?: boolean;
}

/** ¿Puede este usuario abrir 'moduleKey/tab'? (misma regla que ModuleRoute) */
export function canOpen(target: string, a: Access): boolean {
  const [mk, tab] = target.split('/');
  const mod = a.availableModules.find((m) => m.key === mk);
  if (!mod) return false;
  const item = mod.sidebarItems.find((i) => i.tab === tab);
  if (!item || item.comingSoon) return false;
  if (item.requireAdmin && !a.isAdmin && !a.isDev) return false;
  if (item.devOnly && !a.isDev) return false;
  if (item.hideForSovereign && a.sovereign) return false;
  return true;
}

export function resolveTarget(item: ShellNavItem, a: Access): string | null {
  if (item.adminOnly && !a.isAdmin && !a.isDev) return null;
  if (item.hideForSovereign && a.sovereign) return null;
  if (item.requiresModule && !a.availableModules.some((m) => m.key === item.requiresModule)) return null;
  return item.targets.find((t) => canOpen(t, a)) ?? null;
}

export function ownsRoute(item: ShellNavItem, moduleKey?: string, tab?: string): boolean {
  if (!moduleKey) return false;
  return item.owns.some((o) => (o.includes('/') ? o === `${moduleKey}/${tab ?? ''}` : o === moduleKey));
}

export function toPath(target: string): string {
  return `/m/${target}`;
}

/** «Cambios del sitio» es de administradores (aprobar lo exige el backend). */
export function canReviewSite(a: Access): boolean {
  return (a.isAdmin || a.isDev) && canOpen('orion/cambios-sitio', a);
}
